import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";

export function usePushNotifications() {
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const { tenantId } = useTenant();

  useEffect(() => {
    if ("Notification" in window) {
      setPermission(Notification.permission);
    }
    // Check if already subscribed
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then(async (reg: any) => {
        const sub = await reg.pushManager?.getSubscription();
        setSubscribed(!!sub);
      });
    }
  }, []);

  const subscribe = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      console.warn("Push not supported");
      return false;
    }

    setLoading(true);
    try {
      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") return false;

      // Get VAPID key
      const { data: vapidData } = await supabase.functions.invoke("push-notifications", {
        body: { action: "get_vapid_key" },
      });
      if (!vapidData?.vapid_public_key) {
        console.error("No VAPID key");
        return false;
      }

      // Subscribe
      const reg = await navigator.serviceWorker.ready;
      const sub = await (reg as any).pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidData.vapid_public_key),
      });

      // Send to backend
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) return false;

      await supabase.functions.invoke("push-notifications", {
        body: {
          action: "subscribe",
          subscription: sub.toJSON(),
          tenant_id: tenantId,
        },
      });

      setSubscribed(true);
      return true;
    } catch (e) {
      console.error("Push subscribe error:", e);
      return false;
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  return { permission, subscribed, subscribe, loading };
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
